import { useCallback, useEffect, useMemo, useState } from 'react';
import type { JSX, ReactNode } from 'react';
import type { TakiConfig } from '@taki/runtime-config';
import { isAuthOnEmulator, signInWithEmail, useUser } from '@taki/auth';
import { AccesRefuseScreen } from './AccesRefuseScreen.js';
import {
  connexionLocaleDe,
  doitEntrerSeul,
  entreeDejaJouee,
  marquerEntreeJouee,
} from './connexionLocale.js';
import { LoginScreen } from './LoginScreen.js';
import { entreeRefusee } from './refusEntree.js';
import { BootScreen } from './surfaces/BootScreen.js';

/**
 * L'ECRAN DE DEMARRAGE N'EST MONTRE QU'UNE FOIS PAR CHARGEMENT DE PAGE.
 *
 * Au niveau du module, et pour la meme raison que le verrou de l'entree
 * automatique (`connexionLocale.ts`) : ce composant se DEMONTE quand l'hote le
 * retire de l'arbre, et une `ref` repartait alors a zero. Le splash revenait
 * donc en plein milieu d'une session, ce que l'en-tete ci-dessous promet
 * justement de ne jamais faire.
 */
let dejaDemarre = false;

export interface AuthGuardProps {
  config: TakiConfig;
  children: ReactNode;
}

/**
 * Gates the shell on authentication state from `@taki/auth`'s `useUser()`:
 *  - on the very first auth resolution, render the {@link BootScreen} splash;
 *  - when no user is connected, render {@link LoginScreen};
 *  - otherwise render the protected children (the shell).
 *
 * Boot latch: `useUser().loading` starts `true`, flips `false` once the first
 * auth state resolves, and can flip back to `true` on a genuine re-auth (a
 * fresh sign-in after a sign-out). The boot screen must never come back after
 * startup, so `bootedRef` latches once we ever leave the loading state: a
 * later loading phase keeps the shell mounted (the user is already set) rather
 * than re-flashing the splash.
 *
 * Entree automatique en DEV LOCAL : quand la config porte un compte
 * d'emulateur ET qu'Auth parle vraiment a un emulateur, le guard entre seul,
 * sans saisie. Le pourquoi et le double verrou vivent dans `connexionLocale.ts`.
 *
 * LE TROISIEME ETAT, depuis que Google entre ici : AUTHENTIFIE MAIS PAS
 * INVITE. S'authentifier ne donne plus l'acces, parce qu'un compte Google est
 * gratuit et que Firebase le signe sans rien demander a personne. Quand la
 * porte (`rattacherCompte`, cote serveur, interrogee par `useUser`) a dit non,
 * le guard montre le refus au lieu des enfants : sans ca, le shell monterait
 * pour quelqu'un dont chaque requete serait refusee par les rules, et il
 * lirait « erreur » partout au lieu de « tu n'es pas invite ici ».
 */
export function AuthGuard({ config, children }: AuthGuardProps): JSX.Element {
  const { user, loading, rattachement } = useUser();

  // Monotonic latch: true forever once the first resolution happens.
  if (!loading) dejaDemarre = true;

  /*
   * Le compte local, memorise : sans `useMemo`, l'objet serait neuf a chaque
   * rendu et l'effet ci-dessous se relancerait sans arret. `isAuthOnEmulator()`
   * est lu ICI, au moment de la decision : il ne devient vrai qu'apres
   * `initAuth`, donc apres le premier rendu de l'hote.
   */
  const surEmulateur = isAuthOnEmulator();
  const compte = useMemo(
    () => connexionLocaleDe(config, surEmulateur),
    [config, surEmulateur],
  );

  const [entreeEnCours, setEntreeEnCours] = useState(false);

  /*
   * QUI A ETE CONNECTE SUR CETTE PAGE N'Y RENTRE PLUS TOUT SEUL.
   *
   * La regle n'est pas « une tentative par guard », c'est « on n'entre seul que
   * dans une page ou PERSONNE n'etait entre ». Avoir vu un utilisateur suffit
   * donc a fermer le verrou : ce qui vient apres est un depart VOLONTAIRE, et
   * il doit tenir. Depuis que l'ecran de connexion porte un bouton Google, ca
   * compte double : « Essayer une autre adresse », apres un refus, doit rendre
   * la main, pas reconnecter le compte de demo.
   *
   * Le verrou vit dans `connexionLocale.ts`, au niveau du MODULE, et pas dans
   * une `ref` d'ici : ce composant se DEMONTE pour de vrai (l'hote le retire de
   * l'arbre le temps de lire la fiche membre), ce qui rouvrait le verrou au
   * pire moment. Le detail est ecrit la-bas.
   */
  if (user !== null) marquerEntreeJouee();

  const entreeAFaire = doitEntrerSeul({
    compte,
    loading,
    connecte: user !== null,
    dejaTente: entreeDejaJouee(),
  });

  useEffect(() => {
    /*
     * Le verrou est relu ICI, et pas seulement au rendu : `<StrictMode>` rejoue
     * l'effet une seconde fois au montage, avec le meme `entreeAFaire` fige
     * dans la fermeture du premier rendu. Sans cette ligne, l'hote de dev
     * (`apps/hello`, sous StrictMode) lancerait DEUX connexions.
     */
    if (entreeDejaJouee()) return;
    if (!entreeAFaire || compte === null) return;
    marquerEntreeJouee();
    setEntreeEnCours(true);
    void signInWithEmail(compte.email, compte.password)
      .catch((cause: unknown) => {
        // On n'affiche rien de plus : l'ecran de connexion prend le relais et
        // dit lui-meme ce qui s'est passe. Le detail va en console, pas dans
        // une banniere que personne ne peut corriger depuis l'interface.
        console.error(
          "[@taki/shell] entree automatique impossible avec le compte local de l'emulateur.",
          cause,
        );
      })
      .finally(() => {
        setEntreeEnCours(false);
      });
  }, [entreeAFaire, compte]);

  /* Rejouer la verification : c'est un rechargement de page, et c'est voulu.
     Le verdict vit dans `useUser`, dont l'abonnement ne se rejoue pas a la
     demande ; recharger repasse par le meme chemin, proprement, sans un etat
     de plus a tenir pour un cas qui ne se produit qu'en panne. */
  const reessayer = useCallback(() => {
    window.location.reload();
  }, []);

  if (loading && !dejaDemarre) {
    return <BootScreen />;
  }

  /* AVANT LES ENFANTS, ET APRES `loading` : le verdict n'existe qu'une fois
     l'auth resolue, et il prime sur tout le reste. */
  if (user && rattachement !== null && entreeRefusee(rattachement)) {
    return (
      <AccesRefuseScreen
        config={config}
        verdict={rattachement}
        email={user.email}
        onReessayer={reessayer}
      />
    );
  }

  if (!user) {
    /*
     * Tant qu'une entree automatique est decidee ou en vol, on tient le splash
     * plutot que le formulaire : sinon il apparait une fraction de seconde
     * avant de disparaitre, juste assez pour faire croire a un echec.
     * `entreeAFaire` est calcule AVANT que l'effet ne parte : c'est lui qui
     * evite le clignotement du tout premier rendu.
     */
    if (entreeAFaire || entreeEnCours) {
      return <BootScreen />;
    }
    return <LoginScreen config={config} />;
  }

  return <>{children}</>;
}
